import React, { useState, useEffect } from 'react';
import PublicFooter from '../components/PublicFooter';
import { useBlogs } from '../hooks/useBlogs';
import { useAuth } from '../contexts/AuthContext';
import { Blog } from '../types';
import BlogCard from '../components/BlogCard';
import BlogDetailsModal from '../components/BlogDetailsModal';
import CustomDropdown from '../components/CustomDropdown';
import AIChatButton from '../components/AIChatButton';

import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const BlogPage: React.FC = () => {
  const { 
    blogs, 
    loading, 
    error, 
    hasMore, 
    fetchBlogs, 
    loadMoreBlogs, 
    searchBlogs, 
    getCategories, 
    refreshBlogs
  } = useBlogs();
  const { currentUser } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [showBlogModal, setShowBlogModal] = useState(false);

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      const cats = await getCategories();
      setCategories(cats);
    };
    loadCategories();
  }, []); // Remove getCategories from dependency array

  // Apply filters and search
  const applyFilters = () => {
    const filters: any = {};
    
    if (selectedCategory !== 'all') {
      filters.category = selectedCategory;
    }
    
    fetchBlogs(filters);
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      searchBlogs(searchTerm);
    } else {
      fetchBlogs();
    }
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedCategory('all');
    setSearchTerm('');
    fetchBlogs();
  };

  // Handle blog card click
  const handleBlogClick = (blog: Blog) => {
    setSelectedBlog(blog);
    setShowBlogModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            {/* Left - Text */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <DocumentTextIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Knowledge Hub
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Blog</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Insights & Updates
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Discover expert tips, study strategies, and product updates to power your learning journey.
              </p>

              {/* Stats & CTAs */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">{blogs.length}</div>
                  <div className="text-xs text-blue-200">Posts</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">{categories.length}</div>
                  <div className="text-xs text-blue-200">Categories</div>
                </div>
                <div className="text-center">
                  <button
                    onClick={() => { const el = document.getElementById('blog-search') as HTMLInputElement; el?.focus(); }}
                    className="inline-flex items-center justify-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-xs"
                  >
                    <MagnifyingGlassIcon className="h-4 w-4 mr-1" /> Search
                  </button>
                </div>
              </div>
            </div>

            {/* Right - Visuals (desktop only) */}
            <div className="relative hidden xl:block">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <DocumentTextIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Latest Posts</h3>
                      <p className="text-blue-200 text-sm">Stay up to date</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <FunnelIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Smart Filters</h3>
                      <p className="text-blue-200 text-sm">Find what matters</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6">
        {/* Enhanced Search and Filters */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8 mb-12">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search Bar */}
            <div className="flex-1">
              <form onSubmit={handleSearch} className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
                <input
                  id="blog-search"
                  type="text"
                  placeholder="Search blogs by title, content, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-lg"
                />
              </form>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden px-6 py-4 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:bg-gray-50 transition-all duration-300 flex items-center space-x-3 font-medium"
            >
              <FunnelIcon className="h-5 w-5" />
              <span>Filters</span>
            </button>

            {/* Refresh Button */}
            <button
              onClick={refreshBlogs}
              disabled={loading}
              className="px-6 py-4 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 flex items-center space-x-3 font-medium"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {/* Enhanced Filters Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Category
                  </label>
                  <CustomDropdown
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    options={[
                      { value: "all", label: "All Categories" },
                      ...categories.map((category) => ({
                        value: category,
                        label: category
                      }))
                    ]}
                    placeholder="All Categories"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300"
                  />
                </div>

                {/* Apply Filters Button */}
                <div className="flex items-end">
                  <button
                    onClick={applyFilters}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    Apply Filters
                  </button>
                </div>

                {/* Reset Filters Button */}
                <div className="flex items-end">
                  <button
                    onClick={resetFilters}
                    className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300 font-semibold"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Blog Grid */}
        {error ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <DocumentTextIcon className="h-10 w-10 text-red-600" />
            </div>
            <div className="text-red-600 text-xl font-semibold mb-3">Error loading blogs</div>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={refreshBlogs}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg"
            >
              Try Again
            </button>
          </div>
        ) : blogs.length === 0 && !loading ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <DocumentTextIcon className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No blogs found</h3>
            <p className="text-gray-600 text-lg">
              {searchTerm ? 'Try adjusting your search terms or filters.' : 'Check back later for new content.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {blogs.map((blog) => (
              <BlogCard
                key={blog.id}
                blog={blog}
                onClick={() => handleBlogClick(blog)}
              />
            ))}
          </div>
        )}

        {/* Enhanced Load More Button */}
        {hasMore && blogs.length > 0 && (
          <div className="text-center">
            <button
              onClick={loadMoreBlogs}
              disabled={loading}
              className="px-10 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3 inline-block"></div>
                  Loading More Blogs...
                </>
              ) : (
                'Load More Blogs'
              )}
            </button>
          </div>
        )}

        {/* Call to Action Section */}
        {!currentUser && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-center text-white mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Start Learning?
            </h2>
            <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
              Join thousands of students preparing for ICAG and CITG professional qualifications. 
              Get access to expert-led courses and comprehensive study materials.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Create Free Account
              </Link>
              <Link
                to="/signin"
                className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}

        {/* Blog Details Modal */}
        <BlogDetailsModal
          isOpen={showBlogModal}
          onClose={() => {
            setShowBlogModal(false);
            setSelectedBlog(null);
          }}
          blog={selectedBlog}
        />
      </div>
      {/* Footer visible on desktop only via hidden md:block in component */}
      <PublicFooter />
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default BlogPage;
